import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import Typography from '@material-ui/core/Typography';
import { Link as InternalLink } from 'react-router-dom';
import Link from '@material-ui/core/Link';
import MailTo from './MailTo';
import * as URI from 'uri-js';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  link: {
    color: theme.palette.primary.main,
    fontWeight: '500',
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.primary.dark,
      textDecoration: 'underline',
    },
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& thead th': {
      color: '#264f73',
      fontWeight: '900',
      backgroundColor: 'rgb(244, 245, 247)',
      borderColor: 'rgb(193, 199, 208)',
      borderStyle: 'solid',
      borderWidth: '1px',
      textAlign: 'left',
      padding: '8px 8px 8px 10px',
    },
    '& tbody td': {
      padding: '8px',
      borderColor: 'rgb(193, 199, 208)',
      borderStyle: 'solid',
      borderWidth: '1px',
    }
  }
}));

const Header = ({children, level}) => {
  return (
    <Typography variant={'h'+level} color="primary">{children}</Typography>
  );
};
Header.propTypes = {
  children: PropTypes.node.isRequired,
  level: PropTypes.number.isRequired,
};

const Address = ({children, href, ...props}) => {
  const classes = useStyles();
  const uri = URI.parse(href);
  const scheme = uri.scheme;
  // we distinguish three types of links:
  //   relative links are handled with the react-router-dom Link component
  //   mailto links are handled with the MailTo component
  //   other links are handled with the MUI Link component
  if (scheme === undefined) {
    // relative links are <Link to=...>
    return <InternalLink to={uri.path} className={classes.link} {...props}>{children}</InternalLink>;
  } else if (scheme === 'mailto') {
    return <MailTo email={uri.to[0]} className={classes.link} {...props}>{children}</MailTo>;
  } else {
    return <Link href={href} className={classes.link} underline="always" target="_blank" rel="noopener noreferrer" {...props}>{children}</Link>;
  }
};
Address.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  props: PropTypes.object,
};

const Table = ({columnAlignment, ...props}) => {  // eslint-disable-line no-unused-vars
  const classes = useStyles();
  return <table className={classes.table} {...props}/>;
};
Table.propTypes = {
  columnAlignment: PropTypes.array,  // passed by markdown, ignored in our renderer.
};

const renderers = {
  'heading': Header,
  'link': Address,
  'table': Table,
};

const Markdown = ({content, ...props}) => {
  return (
    <ReactMarkdown source={content} escapeHtml={false} renderers={renderers} {...props}/>
  );
};

Markdown.propTypes = {
  content: PropTypes.string,
};

export default Markdown;
